import React, { useEffect, useRef } from "react";
import { motion, useInView, animate } from "framer-motion";
import { CalendarDays, Users, Trophy, School, BarChart2 } from "lucide-react";
import AsOfNote from "./AsOfNote";
import { impact } from "../data/stats";
import "./ImpactSection.scss";

const STATS = [
  { icon: CalendarDays, num: impact.workshops.n, suffix: "+", label: impact.workshops.label, id: "workshops" },
  { icon: Users,        num: impact.students.n,  suffix: "+", label: impact.students.label,  id: "students"  },
  { icon: Trophy,       num: impact.stories.n,   suffix: "+", label: impact.stories.label,   id: "successStories" },
  { icon: School,       num: impact.schools.n,   suffix: "+", label: impact.schools.label,   id: "schools"   },
];

function CountUp({ to, suffix, delay = 0 }) {
  const ref    = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.8,
      delay,
      ease: [0.22, 1, 0.36, 1],
      onUpdate(v) {
        if (ref.current) {
          ref.current.textContent =
            to >= 1000
              ? Math.round(v).toLocaleString("en-US") + suffix
              : Math.round(v) + suffix;
        }
      },
    });
    return controls.stop;
  }, [inView]);

  return (
    <span ref={ref} className="imp__num-val">
      0{suffix}
    </span>
  );
}

const cardAnim = (d = 0) => ({
  initial:     { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport:    { once: true, margin: "-40px" },
  transition:  { duration: 0.4, delay: d, ease: "easeOut" },
});

export default function ImpactSection() {
  return (
    <section className="imp-wrap">

      <div className="imp-inner">

        {/* heading */}
        <motion.div className="imp__top" {...cardAnim(0)}>
          <div className="imp__chip"><BarChart2 size={14} /> OUR IMPACT</div>
          <h2 className="imp__h2">
            Creating Real <span>Impact</span><br />
            Across Classrooms
          </h2>
          <p className="imp__sub">
            Numbers don't lie — every stat here is a life touched,<br />
            a classroom transformed, a future unlocked.
          </p>
        </motion.div>

        {/* 4 stat cards */}
        <div className="imp__grid">
          {STATS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.id}
                className="imp__card"
                {...cardAnim(0.1 + i * 0.09)}
              >
                <span className="imp__ico">
                  <Icon size={22} />
                </span>

                <div className="imp__num" id={s.id}>
                  <CountUp
                    to={s.num}
                    suffix={s.suffix}
                    delay={0.1 + i * 0.09}
                  />
                </div>

                <div className="imp__label">{s.label}</div>

                <div className="imp__deco" aria-hidden="true">
                  <Icon size={120} strokeWidth={0.6} />
                </div>
              </motion.div>
            );
          })}
        </div>

        <AsOfNote className="asof--center" />

      </div>
    </section>
  );
}
